import { useState } from "react";
import { api } from "@/lib/api";
import { X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const METHODS = [
  ["email", "Email reply"],
  ["verbal", "Verbal / phone"],
  ["signed_paper", "Signed paper copy"],
  ["other", "Other"],
];

// Admin records an acceptance that arrived outside the e-sign link so the
// proposal can be converted into a Client + Project.
export default function ManualAcceptModal({ proposal, onClose, onAccepted }) {
  const [f, setF] = useState({ accepted_by_name: proposal.contact_name || "", accepted_by_title: "", method: "email", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/proposals/${proposal.id}/accept-manual`, f);
      const conv = r.data?.auto_convert_result;
      if (conv?.ok) toast.success(`Proposal accepted — project "${conv.project_name}" created automatically. Next: assign workers.`);
      else if (conv) toast.warning(`Proposal accepted, but the project could not be created automatically (${conv.error}). Use Convert.`);
      else toast.success("Proposal marked accepted — you can now convert it.");
      onAccepted?.(r.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 overflow-y-auto" data-testid="manual-accept-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="max-w-lg mx-auto my-16 bg-white rounded-lg shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="tinylabel text-teal-700">Manual acceptance</div>
            <h2 className="text-lg font-bold">Mark "{proposal.company_name}" as accepted</h2>
            <p className="text-xs text-slate-500 mt-1">
              Use this when the client accepted by email, phone or a signed paper copy instead of the e-sign link.
              No electronic signature is recorded and the PDF stays unsigned; the acceptance is audit-logged under your name.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="tinylabel">Accepted by (client name) *</label>
            <input required value={f.accepted_by_name} onChange={(e) => set("accepted_by_name", e.target.value)} data-testid="manual-accept-name"
                   className="w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="Who at the client said yes?" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="tinylabel">Title / position</label>
              <input value={f.accepted_by_title} onChange={(e) => set("accepted_by_title", e.target.value)} data-testid="manual-accept-title"
                     className="w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="e.g. Project Manager" />
            </div>
            <div>
              <label className="tinylabel">How did they accept?</label>
              <select value={f.method} onChange={(e) => set("method", e.target.value)} data-testid="manual-accept-method"
                      className="w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="tinylabel">Note (optional)</label>
            <textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={2} data-testid="manual-accept-note"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md" placeholder="e.g. Approved in email from Jane on 6/12; PO to follow" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button type="submit" disabled={busy} data-testid="manual-accept-submit"
                  className="h-11 px-4 bg-teal-700 hover:bg-teal-800 text-white rounded-md font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <CheckCircle size={16} weight="bold" /> {busy ? "Saving…" : "Mark accepted"}
          </button>
        </div>
      </form>
    </div>
  );
}
